import { createContext, useContext } from 'react'
import { signOut as apiSignOut } from './api'
import { session as devSession, buildVersion as devBuildVersion } from '../app/session'

/** Set by `SessionProvider`. The provider lives in `session.jsx`. */
export const SessionContext = createContext(null)

/** Bootstrap outcome, so a screen can tell "still asking" from "asked, nobody home". */
export const SESSION_STATUS = {
  loading: 'loading',
  authenticated: 'authenticated',
  anonymous: 'anonymous',
  error: 'error',
}

/* ------------------------------------------------------------------ *
 * Why the sign-in screen is showing
 * ------------------------------------------------------------------ */

const SESSION_ENDED_KEY = 'rawam.sessionEnded'

/**
 * A session that ends mid-form used to lose what was typed and say NOTHING.
 *
 * `api.js` calls `onSessionEnded()` and THEN throws, so the screen's own `catch` does raise
 * the outcome dialog — but the full-page `window.location.assign('/login')` is already under
 * way and the dialog never gets a frame. The user arrived at a plain sign-in form with their
 * work gone and no explanation.
 *
 * The redirect carries the reason instead, which is the shape that survives a full page load.
 * `sessionStorage`, not the query string: a `?reason=` would come back on every refresh and
 * bookmark, and this is true exactly once.
 *
 * The content itself is still lost — keeping a draft across a sign-out is a different and much
 * larger thing. This makes the loss explained rather than silent, which is what §10.6
 * XSESS-004 asks for.
 */
export function noteSessionEnded() {
  try {
    sessionStorage.setItem(SESSION_ENDED_KEY, '1')
  } catch {
    // Private windows and blocked site data throw. A missing notice is not worth an error.
  }
}

/** Reads the flag and clears it, so the notice shows once and not on the next refresh. */
export function takeSessionEndedNotice() {
  try {
    const found = sessionStorage.getItem(SESSION_ENDED_KEY) === '1'
    if (found) sessionStorage.removeItem(SESSION_ENDED_KEY)
    return found
  } catch {
    return false
  }
}

/** What `useSession()` answers outside a provider — see below. */
const NO_SESSION = {
  status: SESSION_STATUS.anonymous,
  session: devSession,
  buildVersion: devBuildVersion,
  gate: null,
  warnings: { Policies: false, Surveys: false, Assessments: false },
  error: null,
  reload: () => {},
  revalidate: () => {},
  signOut: apiSignOut,
}

/**
 * The signed-in user and everything the shell hangs off them.
 *
 *   const { session, buildVersion, warnings } = useSession()
 *
 * `session` is the shape `permits()` and `visibleMenu()` in `app/navigation.js` already
 * consume — roles, license feature sets and the settings the layout reads.
 *
 * Outside a provider it falls back to the static development session, so a component can
 * be previewed on its own (`/_kit`) without a running API.
 */
export function useSession() {
  return useContext(SessionContext) ?? NO_SESSION
}
